import { Stack, ButtonBase, useMediaQuery, useTheme } from '@mui/material'
import MaterialSymbol from '../../../../components/MaterialSymbol'
import Modal from '../../../../components/Modal'
import Steppers from '../../../../components/Stepper/Stepper'
import Typography from '../../../../components/Typography'
import { useCampaignContext, withCampaignContextProvider } from './CampaignContext'
import SearchAddressTab from './SearchAddressTab'
import { colors } from '../../../../themes/colors'
import SelectProductTab from './SelectProductTab'
import { QuantityTab } from './QuantityTab'
import { useQuery } from '@tanstack/react-query'
import apiClient from '../../../../services/api-client'
import { SaleAgreementPromo } from './types'
import { AxiosRequestConfig } from 'axios'
import { Loader } from '../../../../components/Loader'
import { useSelector } from 'react-redux'
import { selectRemoteConfig } from '../../../../services/firebase/remote-config/remoteConfig.slice'
import { ReviewTab } from './ReviewTab'
import React, { useEffect, useMemo, useState } from 'react'
import { selectCurrentFarmerAccount } from '../../../azure-b2c/storage'
import YesNoAction from '../../../../components/YesNoAction'
import { Button } from '../../../../components/Button'
import { Keyboard } from '@capacitor/keyboard'
import { Capacitor } from '@capacitor/core'

interface CampaignModalProps {
  campaignId: string | undefined | null
  onModalClosed?: () => void
}

const transformCampaignResponse = (
  data: SaleAgreementPromo,
  hasTfoAccess: boolean,
): SaleAgreementPromo => {
  return {
    ...data,
    editPriceAllowed: data.editPriceAllowed && hasTfoAccess,
    // NOTE: For Testing Quantity Validation
    // minPeriods: 4,
    // products: data.products.map((p) => ({
    //   ...p,
    //   minReleaseQuantity: 3,
    //   maxReleaseQuantity: 999,
    // })),
  }
}

const CampaignModal = ({ campaignId, onModalClosed }: CampaignModalProps) => {
  const remoteConfig = useSelector(selectRemoteConfig)
  const { state, setState, reset } = useCampaignContext()
  const [open, setOpen] = useState<boolean>(Boolean(campaignId))
  const tabViewBgColor = [1, 2, 3].includes(state.tabIndex) ? 'primary25' : undefined
  const isValidCampaignId = typeof campaignId === 'string' && campaignId.length > 0
  const account = useSelector(selectCurrentFarmerAccount)
  const scrollRef = React.useRef<HTMLElement>(null)

  const handleModalClose = () => {
    setOpen(false)
    reset()
    if (onModalClosed) {
      onModalClosed()
    }
  }

  useEffect(() => {
    setOpen(Boolean(campaignId))
  }, [campaignId])

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: 0 })
  }, [state.tabIndex])

  const saleArgreementPromoQuery = useQuery({
    queryKey: ['sale-agreement-promo', campaignId, account?.accountNumber],
    queryFn: async () => {
      const res = await apiClient.get<SaleAgreementPromo, AxiosRequestConfig>(
        `/sales-agreements/promo/${campaignId}?accountNumber=${account?.accountNumber}`,
      )
      return transformCampaignResponse(res.data, Boolean(account?.hasTfoAccess))
    },
    enabled: remoteConfig.magnesiumPromoEnabled && isValidCampaignId,
  })
  const saleAgreementData = saleArgreementPromoQuery.data

  const magenesiumTypeTitle =
    saleAgreementData?.displayTitle ?? saleAgreementData?.documentTitle ?? 'default'

  const agreementNotFound = () => {
    return (
      !saleArgreementPromoQuery.isLoading &&
      !(saleArgreementPromoQuery.isSuccess && saleAgreementData && isValidCampaignId)
    )
  }

  const renderContent = useMemo(() => {
    if (saleArgreementPromoQuery.isLoading) {
      return (
        <Stack direction='column' justifyContent='center' alignItems='center' height='100%'>
          <Loader />
        </Stack>
      )
    }

    if (saleArgreementPromoQuery.isSuccess && saleAgreementData && isValidCampaignId) {
      return (
        <>
          {state.tabIndex === 0 ? <SearchAddressTab /> : null}
          {state.tabIndex === 1 ? (
            <SelectProductTab config={saleAgreementData} campaignId={campaignId} />
          ) : null}
          {state.tabIndex === 2 ? <QuantityTab config={saleAgreementData} /> : null}
          {state.tabIndex === 3 ? <ReviewTab config={saleAgreementData} /> : null}
        </>
      )
    }

    return null
  }, [saleArgreementPromoQuery.isLoading, saleArgreementPromoQuery.isSuccess, state.tabIndex])

  React.useEffect(() => {
    if (saleAgreementData) {
      const product = saleAgreementData.products.find(
        (p) => p.siteId === saleAgreementData.defaultSiteId,
      )

      // set the default site
      if (product) {
        setState({
          siteSelected: {
            siteId: product.siteId,
            siteName: product.siteName,
            sitePostcode: product.sitePostcode,
          },
        })
      }
    }
  }, [saleAgreementData])

  const [keyboardPadding, setKeyboardPadding] = useState(0)

  React.useEffect(() => {
    if (Capacitor.isNativePlatform() && Capacitor.getPlatform() === 'ios') {
      Keyboard.addListener('keyboardDidShow', (info) => {
        setKeyboardPadding(info.keyboardHeight / 10)
      })

      Keyboard.addListener('keyboardWillHide', () => {
        setKeyboardPadding(0)
      })
      return () => void Keyboard.removeAllListeners()
    }
  }, [])

  const theme = useTheme()
  const isSm = useMediaQuery(theme.breakpoints.down('sm'))

  if (remoteConfig.magnesiumPromoEnabled === false) {
    return null
  }

  if (agreementNotFound()) {
    return <CampaignNotFound handleModalClose={handleModalClose} open={open} isSm={isSm} />
  }

  return (
    <Modal
      sx={{
        overflow: 'hidden',
      }}
      title={
        state.tabIndex > 0 ? (
          <ButtonBase
            onClick={() => setState({ tabIndex: state.tabIndex - 1 })}
            sx={{ color: colors.grey700 }}
          >
            <Stack display='inline-flex' flexDirection='row' gap='6px'>
              <MaterialSymbol>west</MaterialSymbol>
              <Typography variant='body2'>Back</Typography>
            </Stack>
          </ButtonBase>
        ) : null
      }
      open={open}
      scrollRef={scrollRef}
      handleClose={handleModalClose}
      header={
        saleArgreementPromoQuery.isSuccess ? (
          <Steppers
            disableStepperClick
            enableBackBtn={false}
            maxSteps={4}
            activeStep={state.tabIndex}
            title={magenesiumTypeTitle}
            previousStep={1}
            setPageIndex={(value) => {
              setState({ tabIndex: value })
            }}
          />
        ) : null
      }
      contentBg={tabViewBgColor}
      headerDivider
      maxWidth='md'
      paperProps={{
        sx: {
          height: {
            sm: 'inherit',
            md: '844px',
          },
          overflow: 'hidden',
        },
      }}
      dialogContentSx={{ paddingBottom: `${keyboardPadding}px` }}
    >
      {renderContent}
    </Modal>
  )
}

type CampaignNotFoundProps = {
  handleModalClose: () => void
  open: boolean
  isSm: boolean
}

const CampaignNotFound: React.FC<CampaignNotFoundProps> = ({ handleModalClose, open, isSm }) => {
  return (
    <Modal
      handleClose={handleModalClose}
      title='Agreement not found'
      open={open}
      footer={
        isSm && (
          <Button sx={{ width: '100%' }} variant='contained' onClick={handleModalClose}>
            Close
          </Button>
        )
      }
    >
      <YesNoAction body='Sorry, this agreement type is no longer available.' />
    </Modal>
  )
}

export default withCampaignContextProvider<CampaignModalProps>(CampaignModal)
